import type {
  IncidentDetailResponse,
  IncidentListResponse,
  IncidentVerdictRequest,
} from '@prerender/diagnostics-api';

import api from '../../api/apiSlice';
import { incidentListQueryString, type IncidentListQueryArgs } from './incidentListQueryArgs';

export type RecordVerdictArgs = IncidentVerdictRequest & { reference: string };

// A verdict changes the counters and every listing as well as the incident itself.
const EVERY_INCIDENT = 'LIST';

const CONFLICT = 409;

const changedTheIncident = (error: unknown): boolean => {
  if (error === undefined) return true;

  const status = (error as { status?: unknown }).status;

  return status === CONFLICT;
};

export const incidentsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    listIncidents: builder.query<IncidentListResponse, IncidentListQueryArgs>({
      query: (args) => `/diagnostics/incidents?${incidentListQueryString(args)}`,
      providesTags: [{ type: 'Incident', id: EVERY_INCIDENT }],
    }),
    getIncident: builder.query<IncidentDetailResponse, string>({
      query: (reference) => `/diagnostics/incidents/${encodeURIComponent(reference)}`,
      providesTags: (_incident, _error, reference) => [{ type: 'Incident', id: reference }],
    }),
    recordVerdict: builder.mutation<void, RecordVerdictArgs>({
      query: ({ reference, ...verdict }) => ({
        url: `/diagnostics/incidents/${encodeURIComponent(reference)}/status`,
        method: 'PUT',
        body: verdict,
      }),
      // RTK Query invalidates on a rejected mutation as readily as an accepted one, and a
      // verdict refused for want of a role would refetch, fail the same way, and replace
      // the page — message and all — with a full-page error. A conflict is the exception
      // worth refetching for: it says the incident moved, so the page is showing a state
      // that is already gone.
      invalidatesTags: (_result, error, { reference }) =>
        changedTheIncident(error)
          ? [
              { type: 'Incident', id: reference },
              { type: 'Incident', id: EVERY_INCIDENT },
            ]
          : [],
    }),
  }),
});

export const {
  useGetIncidentQuery,
  useListIncidentsQuery,
  useRecordVerdictMutation,
} = incidentsApi;
